import { addQueryParamsToUrl } from '@healthhub/react-ui';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CaregiverProfile, CommonProfile } from '@shared/assets';
import { FilledAddCaregiverIcon, FilledAddProfileIcon } from '@shared/assets-icon';
import { RelatedPeopleStatus } from '@shared/constants/constants';
import {
  ADD_CARE_GIVERS_PATH,
  ADD_CARE_RECIPIENT_PATH,
  ADD_CHILDREN_HEALTH_PATH,
  CANCEL_CARE_RECIPIENT_PATH,
  CARE_GIVERS_ADD_FAMILY_FRIEND_PATH,
  MY_CHILDREN_PROFILE_DETAILS_PATH,
  MY_FAMILY_AND_FRIEND_HEALTH_DETAILS_PATH,
} from '@shared/constants/paths';
import { useGetRelatedPeopleQuery } from '@shared/services/caregivers';
import type { RelatedPersonProfile } from '@shared/services/caregivers/types/getRelatedPeople.types';
import { RelatedPersonType } from '@shared/services/caregivers/types/getRelatedPeople.types';
import { isCaregiversHasActionRequired } from '@shared/utils/caregivers';

import { useHandleCheckCaregiverPermission } from '@features/Caregivers/FamilyFriends/Caregivers/shared/hooks';
import {
  formatCleanCareDependantsPath,
  generateBtnTextByErrorCode,
  navigateToManageCaregiver,
} from '@features/Caregivers/FamilyFriends/Caregivers/shared/utils';

import useIsAuthenticated from '../useIsAuthenticated';

export interface OnClickProfileParams {
  setSelectedCareRecipient?: Dispatch<
    SetStateAction<
      | null
      | (RelatedPersonProfile & {
          redirectUrl?: string;
        })
    >
  >;
  user: RelatedPersonProfile;
}

export const useFamilyHealth = () => {
  const { t } = useTranslation('home');
  const { isAuthenticated } = useIsAuthenticated();
  const navigate = useNavigate();
  const { data, error, isFetching, refetch } = useGetRelatedPeopleQuery(
    {
      RelatedPersonType: RelatedPersonType.ALL,
      Sort: 0,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isAuthenticated,
    }
  );

  const isCaregiverActionRequired = isCaregiversHasActionRequired(data?.Caregivers ?? []);

  const { checkPermission, isLoading: isCheckingPermission } = useHandleCheckCaregiverPermission({
    generateBtnText: generateBtnTextByErrorCode,
    modalBtnText: t('ok'),
    defaultErrorMessage: 'You are now allowed to add care recipient',
  });

  const onClickProfile = ({ setSelectedCareRecipient, user }: OnClickProfileParams) => {
    const isRequestPending = user.Status === RelatedPeopleStatus.PENDING;

    if (user.FlowType === RelatedPersonType.CAREGIVERS && isRequestPending) {
      navigate(
        addQueryParamsToUrl(CARE_GIVERS_ADD_FAMILY_FRIEND_PATH, {
          fullName: user.RequesterFullName,
          mid: user.Prn,
        })
      );
      return;
    }
    if (user.FlowType === RelatedPersonType.FAMILY_AND_FRIEND && isRequestPending) {
      navigate(
        addQueryParamsToUrl(CANCEL_CARE_RECIPIENT_PATH, {
          displayName: user.DisplayName,
          id: user.Id,
          mid: user.Prn,
          nric: user.MaskedNric,
          relationship: user.RelationShip,
        })
      );
      return;
    }

    const formattedPath = formatCleanCareDependantsPath(MY_FAMILY_AND_FRIEND_HEALTH_DETAILS_PATH);
    const healthRecordsUrl = addQueryParamsToUrl(formattedPath, { mid: user.Prn });

    if (user.IsShowUpdateHealthRecord) {
      setSelectedCareRecipient?.({
        ...user,
        redirectUrl: healthRecordsUrl,
      });
    } else {
      navigate(healthRecordsUrl);
    }
  };

  const isLoading = isFetching || isCheckingPermission;

  const familyHealth = [
    {
      data: data?.Children,
      description: t('addChildren'),
      icon: <FilledAddProfileIcon className="size-13px shrink-0" />,
      id: RelatedPersonType.CHILDREN,
      isClickable: (profile: RelatedPersonProfile) =>
        !(profile.Status === RelatedPeopleStatus.PENDING && profile.FlowType === RelatedPersonType.CHILDREN),
      isLoading,
      onClickAddNew: async () => {
        const hasPermission = await checkPermission(RelatedPersonType.CHILDREN);

        if (hasPermission) {
          navigate(ADD_CHILDREN_HEALTH_PATH);
        }
      },
      onClickProfile: ({ user }: OnClickProfileParams) => {
        navigate(
          addQueryParamsToUrl(MY_CHILDREN_PROFILE_DETAILS_PATH, {
            mid: user.Prn,
          })
        );
      },
      title: t('myChildren'),
      defaultProfilePicDisplay: CommonProfile,
    },
    {
      addProfileUrl: ADD_CARE_RECIPIENT_PATH,
      data: data?.FamilyAndFriend?.map((profile) => {
        if (profile.FlowType === RelatedPersonType.CAREGIVERS && profile.Status === RelatedPeopleStatus.PENDING) {
          return {
            ...profile,
            DisplayName: profile.RequesterFullName || profile.DisplayName,
          };
        }

        return profile;
      }),
      description: t('addFamilyAndFriends'),
      icon: <FilledAddProfileIcon className="size-13px shrink-0" />,
      id: RelatedPersonType.FAMILY_AND_FRIEND,
      isClickable: () => true,
      isLoading,
      onClickAddNew: async () => {
        const hasPermission = await checkPermission(RelatedPersonType.FAMILY_AND_FRIEND);

        if (hasPermission) {
          navigate(ADD_CARE_RECIPIENT_PATH);
        }
      },
      onClickProfile,
      title: t('myFamilyMembers'),
      defaultProfilePicDisplay: CommonProfile,
    },
    {
      data: data?.Caregivers?.map((profile) => {
        if (
          profile.FlowType === RelatedPersonType.FAMILY_AND_FRIEND &&
          profile.Status === RelatedPeopleStatus.PENDING
        ) {
          return {
            ...profile,
            DisplayName: profile.RequesterFullName || profile.DisplayName,
          };
        }

        return profile;
      }),
      description: t('addCaregiver'),
      icon: <FilledAddCaregiverIcon className="size-13px shrink-0" />,
      id: RelatedPersonType.CAREGIVERS,
      isClickable: () => true,
      isLoading,
      onClickAddNew: async () => {
        const hasPermission = await checkPermission(RelatedPersonType.CAREGIVERS);

        if (hasPermission) {
          navigate(ADD_CARE_GIVERS_PATH);
        }
      },
      onClickProfile: ({ user }: OnClickProfileParams) => {
        navigateToManageCaregiver({
          displayName: user.DisplayName,
          email: user.Email ?? '',
          id: user.Id,
          isCaregiverFlow: user.FlowType === RelatedPersonType.CAREGIVERS,
          isRequestPending: user.Status === RelatedPeopleStatus.PENDING,
          mid: user.Prn,
          navigate,
          nric: user.MaskedNric,
        });
      },
      title: t('myCaregivers'),
      defaultProfilePicDisplay: CaregiverProfile,
    },
  ];

  return {
    familyHealth,
    isFetching,
    refetchFamilyProfile: refetch,
    retrieveFamilyProfileError: error,
    isCaregiverActionRequired,
  };
};
